import { ContractOtherCostLimitSpan } from '@components/ContractOtherCostLimitSpan/ContractOtherCostLimitSpan';
import { FileSelectorFormWrapper } from '@components/FilesSelector/FileSelectorFormWrapper';
import { ControlNumberField } from '@components/Form';
import { TooltipWrapper } from '@components/TooltipWrapper';
import type { ConstructionTypeData } from '@gql/graphql';
import {
    Attachment,
    Checkbox,
    cnConsta,
    cnMixSpace,
    FieldLabel,
    Grid,
    GridItem,
    IconAlert,
    IconClose,
    IconQuestion,
    Informer,
    Text,
} from '@uikit';
import { formatBytes, formatDateToLocale, getFileExtention } from '@utils';
import { downloadURI } from '@utils/downloadDataURI';

import cn from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormClearErrors, UseFormSetValue } from 'react-hook-form/dist/types/form';

import Styles from '../BimCalculationLimitedEditModal.module.css';

export const COMPONENT_NAME = 'TextField' as const;
export const cnTextField = cnConsta(COMPONENT_NAME);

interface IBimCalculationLimitedModalPercentTab {
    control: Control<any>;
    selectedConstructionTypes: Record<number, boolean>;
    changeSelectedConstructionTypeHandler: (id: number, newState: boolean) => void;
    currentConstructionTypeId?: number;
    areFieldsDisabled: boolean;
    constructionTypes?: ConstructionTypeData[];
    isTooltipHidden?: boolean;
    clearErrors?: UseFormClearErrors<any>;
    setValue: UseFormSetValue<any>;
    getValues: UseFormGetValues<Record<string, any>>;
    isAdditionalTransport?: boolean;
    isViewModal?: boolean;
    files: File[];
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

const extensionRegExp = /\.(?!.*\.)(\w*)/;

export const BimCalculationLimitedModalPercentTab: React.FC<IBimCalculationLimitedModalPercentTab> = (props) => {
    const {
        control,
        selectedConstructionTypes,
        changeSelectedConstructionTypeHandler,
        currentConstructionTypeId,
        areFieldsDisabled,
        constructionTypes,
        isTooltipHidden,
        setValue,
        getValues,
        isAdditionalTransport,
        isViewModal,
        files,
        setFiles,
    } = props;

    const constructionTypeCheckHandler = (params: { checked: boolean }, id: number) => {
        changeSelectedConstructionTypeHandler(id, params.checked);
    };

    const constructionTypesSortName = useMemo(
        () => (constructionTypes ? [...constructionTypes].sort((a, b) => a?.name?.localeCompare(b?.name)) : []),
        [constructionTypes],
    );

    const currentName = useWatch({ control, name: 'name' });

    const makeRequestHandler = useCallback(
        (filesToProcess) => {
            setFiles(filesToProcess);
        },
        [setFiles],
    );

    const currentPercent = useWatch({ control, name: 'percent' });

    const onPercentChange = useCallback(
        (value: string) => {
            if (isAdditionalTransport) {
                Object.entries(selectedConstructionTypes).forEach(([type, checked]) => {
                    if (checked) {
                        setValue(`cost.${type}`, value);
                    }
                });
            }
        },
        [isAdditionalTransport, selectedConstructionTypes, setValue],
    );

    useEffect(() => {
        if (isAdditionalTransport) {
            onPercentChange(getValues('percent'));
        }
    }, [getValues, isAdditionalTransport, onPercentChange, selectedConstructionTypes]);

    return (
        <Grid>
            {isAdditionalTransport && (
                <>
                    <GridItem>
                        <Informer
                            status="warning"
                            label="Величина % будет применена к выбранным видам строительства в рамках текущего расчета НМЦ."
                            icon={IconAlert}
                            size="s"
                        />
                    </GridItem>
                    <GridItem className={Styles.marginPercentBlock}>
                        <ControlNumberField
                            name="percent"
                            label="Величина % расчета дополнительной транспортировки"
                            placeholder="0,000"
                            decimalScale={3}
                            type="text"
                            control={control}
                            required
                            size="s"
                            rules={{
                                required: 'Обязательное поле',
                            }}
                            disabled={isViewModal}
                            isTooltipHidden={!!isTooltipHidden}
                            width="default"
                            className={Styles.shrinkTextField}
                            onChangeField={onPercentChange}
                        />
                    </GridItem>
                </>
            )}
            <GridItem className={cn(Styles.labelBlock, Styles.marginBlock)}>
                <span>Вид строительства</span>
                <span className={cn({ [Styles.priceLabel]: true, [Styles.priceLabel_limited]: false })}>
                    % лимитированных
                </span>
            </GridItem>
            {constructionTypesSortName?.map(({ id, name }) => {
                const defaultConstruction = id === currentConstructionTypeId;
                const isCheckDisabled = defaultConstruction || areFieldsDisabled || isViewModal;
                const isChecked = selectedConstructionTypes[id] ?? false;
                const isDisabled = areFieldsDisabled || !isChecked;
                const isReadOnlyPercent = isViewModal || isAdditionalTransport;
                const label = (
                    <Checkbox
                        id={String(id)}
                        onChange={(params) => constructionTypeCheckHandler(params, id)}
                        checked={isChecked}
                        disabled={isCheckDisabled}
                        style={{ marginRight: 10, marginTop: isReadOnlyPercent ? 0 : 5, cursor: 'pointer' }}
                        label={name}
                        className={cn({
                            [`${Styles.checkboxMarginRight} FieldLabel TextField-Label`]: isAdditionalTransport,
                        })}
                    />
                );

                return (
                    <GridItem key={id} className={Styles.marginBlock}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div className={Styles.wrapperContractLimitSpan} style={{ minWidth: '100%' }}>
                                {isReadOnlyPercent ? (
                                    <div className={Styles.percentViewRow}>
                                        {label}
                                        <Text size="s">{(isChecked ? currentPercent : null) || '0,000'}</Text>
                                    </div>
                                ) : (
                                    <ContractOtherCostLimitSpan
                                        label={label}
                                        control={control}
                                        costRounding={3}
                                        defaultConstruction={defaultConstruction}
                                        constructionTypeId={id}
                                        constructionTypeName={name}
                                        isDisabled={isDisabled}
                                        isChecked={isChecked}
                                        isTooltipHidden={!!isTooltipHidden}
                                        setValue={setValue}
                                        noLimitSpan
                                        name={currentName}
                                        inputClassName={Styles.contractOtherCostLimitNoSpanInput}
                                        isFullWidth
                                        isEmptyPlaceholder={isDisabled}
                                    />
                                )}
                            </div>
                        </div>
                    </GridItem>
                );
            })}
            <GridItem>
                {isAdditionalTransport && (
                    <div className={Styles.fileSelectorLabel}>
                        <FieldLabel size="s" className={cnTextField('Label', ['TextField-Label'])}>
                            Загрузка расчета доп.транспорта
                        </FieldLabel>
                        <TooltipWrapper
                            direction="upCenter"
                            isHidden={false}
                            toolTipStyle={{ zIndex: 9999 }}
                            message='Для корректного считывания данных в загружаемом шаблоне должно содержаться значение по величине % в столбце "Кол-во" строки "Итого доставка МТР"'
                        >
                            <IconQuestion size="s" style={{ marginLeft: '8px', color: '#00203399' }} />
                        </TooltipWrapper>
                    </div>
                )}
                {isViewModal &&
                    files?.map((file) => (
                        <Attachment
                            key={file.name}
                            fileName={file.name.replace(extensionRegExp, '')}
                            fileExtension={file.name.match(extensionRegExp)?.[1] || getFileExtention(file.type)}
                            fileDescription={`${formatBytes(file.size)} ${formatDateToLocale(
                                new Date(file.lastModified),
                                true,
                            )}`}
                            buttonIcon={IconClose}
                            className={cnMixSpace({ mT: 'xs' })}
                            onClick={(e: any) => {
                                e?.stopPropagation();
                                const reader = new FileReader();

                                reader.addEventListener(
                                    'load',
                                    () => downloadURI(reader.result as string, file.name),
                                    false,
                                );
                                reader.readAsDataURL(file);
                            }}
                        />
                    ))}
                {isAdditionalTransport && !isViewModal && (
                    <FileSelectorFormWrapper
                        name="files"
                        control={control}
                        isTooltipHidden={!!isTooltipHidden}
                        accept={['.xlsx']}
                        withoutEndingOfWordFile
                        onFilesChange={makeRequestHandler}
                        isExternalFilesState
                        files={files}
                        withDownloadAction
                        isTrashDeleteIcon
                        locale={{
                            'file-invalid-type': ({ file }) =>
                                `Формат загружаемого файла ${file.name} не соответствует требованиям. Для загрузки используйте файл формата xlsx`,
                        }}
                        classNameWrapper={Styles.fileSelector_limited}
                    />
                )}
            </GridItem>
        </Grid>
    );
};

BimCalculationLimitedModalPercentTab.displayName = 'BimCalculationLimitedModalPercentTab';
